import Check from '@mui/icons-material/Check'
import Close from '@mui/icons-material/Close'
import {
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Box
} from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {useTrackMaintainEvent} from '../../../shared/hooks/useTrackMaintain'
import {useSignalDescription} from '../../general/hooks/useSignalDescription'
import {SignalsListType} from '../types/sensor.types'

export type CombineModalProps = {
  onCancel: () => void
  groupId?: number
  onDone: (combinedSignals: string[]) => void
  selectedSensors: SignalsListType[]
  combinedSignals: string[]
}

export const CombineModal = ({
  onCancel,
  onDone,
  selectedSensors,
  combinedSignals
}: CombineModalProps) => {
  const {t} = useTranslation()
  const [currentGroup, setCurrentGroup] = useState<string[]>(combinedSignals)
  const getSignalDescription = useSignalDescription()
  const trackMaintainEvent = useTrackMaintainEvent()

  useEffect(() => {
    trackMaintainEvent({name: 'kmsCombinedView'})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isSensorChecked = (sensorId: string) => currentGroup.includes(sensorId)

  const onCheck = (sensorId: string, checked: boolean) => {
    setCurrentGroup((group) => {
      if (checked) {
        return [...group, sensorId]
      } else {
        return group.filter((id) => id !== sensorId)
      }
    })
  }

  const onSubmit = () => {
    onDone(currentGroup)
  }

  return (
    <Dialog open={true} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle component="div" flexDirection="column" display="flex">
        <Typography variant="h2">{t('sensorDetails.compare')}</Typography>
      </DialogTitle>
      <DialogContent sx={{display: 'flex', gap: 4, flexDirection: 'column'}}>
        <Typography variant="caption" fontSize={'16px'}>
          {t('combine.selectGraphs')}
        </Typography>
        <Box sx={{maxHeight: '400px', overflowY: 'scroll'}}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('combine.signalName')}</TableCell>
                <TableCell>{t('combine.signalId')}</TableCell>
                <TableCell>{t('combine.signalUnit')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedSensors.map((sensor) => (
                <TableRow key={`${sensor.localName}-combine-graph`}>
                  <TableCell>
                    <Checkbox
                      size="small"
                      checked={isSensorChecked(sensor.localName)}
                      onChange={(e) => onCheck(sensor.localName, e.target.checked)}
                    />
                    {sensor.localName}
                  </TableCell>
                  <TableCell>
                    {getSignalDescription({description: sensor.description, name: sensor.name})}
                  </TableCell>
                  <TableCell>{sensor.unit.name ?? sensor.unit}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
      <DialogActions sx={{justifyContent: 'space-between', alignItems: 'center'}}>
        <Typography variant={'h4'}>
          {currentGroup.length > 0 ? currentGroup.length : '0'} {t('combine.graphsSelected')}
        </Typography>
        <Box>
          <Button startIcon={<Close />} variant="outlined" color="secondary" onClick={onCancel}>
            {t('general.cancel')}
          </Button>
          <Button
            startIcon={<Check />}
            color="primary"
            variant="contained"
            disabled={currentGroup.length < 2}
            onClick={() => onSubmit()}
            sx={{marginLeft: 1}}
          >
            {t('general.combine')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
